import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Check, User } from 'lucide-react';
import WYSIWYGEditor from '../forms/WYSIWYGEditor';
import PriorityDropdown from '../forms/PriorityDropdown';
import SearchableSelect from '../ui/SearchableSelect';
import apiService from '../../services/api';
import { StepDetailsProps, CompanyUser, WorkOrderTemplate, Category, ClientLocationDto } from '../../types';
import { formatPhoneNumber, formatCurrency, parseCurrency } from '../../utils/formatters';

const schema = yup.object({
  assignedUserId: yup.string().optional(),
  priorityId: yup.number().required('Priority is required').min(1, 'Please select a priority'),
  statusId: yup.number().required('Status is required').min(1, 'Please select a status'),
  categoryId: yup.number().optional(),
  equipmentId: yup.number().optional(),
  dueDate: yup.string().required('Due date is required'),
  externalWorkOrderNumber: yup.string(),
  templateId: yup.number().optional(),
  nteAmount: yup.number().optional().min(0, 'NTE amount must be positive'),
  description: yup.string().optional()
});

type FormData = yup.InferType<typeof schema>;

const StepDetails: React.FC<StepDetailsProps> = ({ 
  data, 
  onChange, 
  onNext, 
  onPrevious,
  isFirstStep, 
  isLastStep, 
  isSubmitting 
}) => {
  const [isValid, setIsValid] = useState(false);
  const [displayLocation, setDisplayLocation] = useState<{name?: string; city?: string; state?: string; zip?: string}>({});

  useEffect(() => {
    const has = !!(data.selectedClientLocationName || data.selectedClientLocationCity || data.selectedClientLocationState || data.selectedClientLocationZipCode);
    if (has) {
      setDisplayLocation({
        name: data.selectedClientLocationName,
        city: data.selectedClientLocationCity,
        state: data.selectedClientLocationState,
        zip: data.selectedClientLocationZipCode
      });
      return;
    }
    const fetchLoc = async () => {
      try {
        if (!data.selectedClient?.id) return;
        const res = await apiService.getClientLocations(data.selectedClient.id);
        const list = (res.data || []) as ClientLocationDto[];
        const loc = list.find(l => l.id === data.selectedClientLocationId) || list.find(l => l.isPrimary);
        if (loc) setDisplayLocation({ name: loc.locationName, city: loc.city, state: loc.state, zip: loc.zipCode });
      } catch {}
    };
    fetchLoc();
  }, [data.selectedClient?.id, data.selectedClientLocationId, data.selectedClientLocationName, data.selectedClientLocationCity, data.selectedClientLocationState, data.selectedClientLocationZipCode]);


  // Query for company users
  const {
    data: usersResponse,
    isLoading: usersLoading,
    error: usersError
  } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers(),
    retry: 1,
    retryDelay: 1000
  });

  const users = usersResponse?.data || [];

  // Query for active templates
  const {
    data: templatesResponse,
    isLoading: templatesLoading,
    error: templatesError
  } = useQuery({
    queryKey: ['active-templates'],
    queryFn: () => apiService.getActiveWorkOrderTemplates(),
    retry: 1
  });

  const templates = templatesResponse?.data || [];

  // Query for active categories
  const {
    data: categoriesResponse,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ['active-categories'],
    queryFn: () => apiService.getActiveCategories(),
    retry: 1
  });

  const categories = categoriesResponse?.data || [];

  // Query for equipment based on selected client and location
  const {
    data: equipmentResponse,
    isLoading: equipmentLoading,
    error: equipmentError
  } = useQuery({
    queryKey: ['equipment-for-location', data.selectedClient?.id, data.selectedClientLocationId],
    queryFn: async () => {
      const response = await apiService.getEquipment(1, 1000);
      const allEquipment = response.data?.data || [];
      // Filter by client and location if selected
      return allEquipment.filter((eq: any) => {
        if (data.selectedClient && eq.clientId !== data.selectedClient.id) return false;
        if (data.selectedClientLocationId && eq.clientLocationId !== data.selectedClientLocationId) return false;
        return true;
      });
    },
    enabled: !!data.selectedClient?.id,
    retry: 1
  });

  const equipment = equipmentResponse || [];

  // Query for vendor details
  const {
    data: vendorsResponse,
    isLoading: vendorsLoading,
    error: vendorsError
  } = useQuery({
    queryKey: ['vendors', 'details', data.selectedVendors?.map(v => v.vendorId)],
    queryFn: async () => {
      if (!data.selectedVendors || data.selectedVendors.length === 0) return [];
      
      // Fetch details for each selected vendor
      const vendorPromises = data.selectedVendors.map(vendor => 
        apiService.getVendor(vendor.vendorId)
      );
      
      const vendorDetails = await Promise.all(vendorPromises);
      return vendorDetails.map(response => response.data);
    },
    enabled: !!(data.selectedVendors && data.selectedVendors.length > 0),
    retry: 1
  });

  const vendors = vendorsResponse || [];

  // Form setup
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid: formIsValid }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      assignedUserId: data.assignedUserId || '',
      priorityId: data.priorityId || 0,
      statusId: data.statusId || 0,
      categoryId: data.categoryId || undefined,
      equipmentId: data.equipmentId || undefined,
      dueDate: data.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      externalWorkOrderNumber: data.externalWorkOrderNumber || '',
      templateId: data.templateId || undefined,
      nteAmount: data.nteAmount || undefined,
      description: data.description || ''
    },
    mode: 'onChange'
  });

  // Watch form values for validation
  const watchedValues = watch();

  // Helper function to get text length from HTML
  const getTextLength = (html: string) => {
    if (!html) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.length || 0;
  };

  // Update validation state
  useEffect(() => {
    setIsValid(formIsValid);
  }, [formIsValid]);

  // Initialize editor with existing description
  // Description will be handled by the WYSIWYGEditor component

  const onSubmit = (formData: FormData) => {
    // Transform empty strings to undefined for optional fields
    const transformedData = {
      ...formData,
      categoryId: formData.categoryId === '' ? undefined : formData.categoryId,
      equipmentId: formData.equipmentId === '' ? undefined : formData.equipmentId,
      templateId: formData.templateId === '' ? undefined : formData.templateId
    };
    console.log('Step 3 form data:', transformedData);
    onChange(transformedData);
    onNext();
  };

  const handleBack = () => {
    onChange(watchedValues);
    onPrevious();
  };


  // Fetch statuses from backend
  const { data: statusesResponse, isLoading: statusesLoading, error: statusesError } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses()
  });

  const statusOptions = statusesResponse?.data?.map(status => ({
    value: status.id,
    label: status.name
  })) || [];

  // Error handling
  if (usersError || statusesError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Error loading data</div>
        <div className="text-gray-500 mt-2">Please try again</div>
      </div>
    );
  }

  // Check if we have the required data from previous steps
  if (!data.selectedClient) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Missing required data from previous steps</div>
        <div className="text-gray-500 mt-2">Please go back and complete the client selection</div>
        <button
          type="button"
          onClick={onPrevious}
          className="mt-4 btn-secondary"
        >
          Back to Previous Step
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Client Summary */}
      <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-700 rounded-md p-4">
        <h4 className="text-sm font-medium text-accent-color-700 dark:text-accent-color-300 mb-3 flex items-center">
          <User className="h-4 w-4 mr-2" />
          Selected Client
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-accent-color dark:text-accent-color-400 font-medium">Client:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">{data.selectedClient.name}</span>
          </div>
          <div>
            <span className="text-accent-color dark:text-accent-color-400 font-medium">Location:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">
              {(displayLocation.name && displayLocation.name.replace(' - Primary Location',''))
                || (data.selectedClientLocationName && data.selectedClientLocationName.replace(' - Primary Location',''))
                || (data.selectedClient.primaryLocationName && data.selectedClient.primaryLocationName.replace(' - Primary Location',''))
                || '-'}
            </span>
          </div>
          <div>
            <span className="text-accent-color dark:text-accent-color-400 font-medium">City/State/Zip:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">
              {(() => {
                const parts = [
                  displayLocation.city || data.selectedClientLocationCity || data.selectedClient.primaryLocationCity || data.selectedClient.city,
                  displayLocation.state || data.selectedClientLocationState || data.selectedClient.primaryLocationState || data.selectedClient.state,
                  displayLocation.zip || data.selectedClientLocationZipCode || data.selectedClient.primaryLocationZipCode || data.selectedClient.zipCode
                ].filter(Boolean);
                return parts.length > 0 ? parts.join(', ') : '-';
              })()}
            </span>
          </div>
          <div>
            <span className="text-accent-color dark:text-accent-color-400 font-medium">Phone:</span>
            <span className="ml-2 text-accent-color-800 dark:text-accent-color-200">{data.selectedClient.phone ? formatPhoneNumber(data.selectedClient.phone) : '-'}</span>
          </div>
        </div>
      </div>

      {/* Selected Vendors Summary */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md p-4">
        <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-3 flex items-center">
          <User className="h-4 w-4 mr-2" />
          Selected Vendors ({(data.selectedVendors?.length || 0)})
        </h4>
        {(data.selectedVendors?.length || 0) > 0 ? (
          <div className="space-y-3">
            {(data.selectedVendors || []).map((selectedVendor, index) => {
              const vendor = vendors.find(v => v.id === selectedVendor.vendorId);
              
              return (
                <div key={selectedVendor.vendorId} className="bg-white dark:bg-gray-800 border border-green-200 dark:border-green-600 rounded-md p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {vendor ? vendor.name : `Vendor ID: ${selectedVendor.vendorId}`}
                        </span>
                        {selectedVendor.isPrimary && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent-color-100 dark:bg-accent-color-800 text-accent-color-800 dark:text-accent-color-200">
                            Primary Vendor
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div>
                          <span className="font-medium">Status:</span>
                          <span className="ml-1">{selectedVendor.status}</span>
                        </div>
                        <div>
                          <span className="font-medium">Location:</span>
                          <span className="ml-1">
                            {vendor ? `${vendor.city}, ${vendor.state} ${vendor.zipCode}` : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Rating:</span>
                          <span className="ml-1">
                            {vendor && vendor.rating ? `${vendor.rating.toFixed(1)}/5` : 'Not rated'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              No vendors selected (optional)
            </div>
            <div className="text-gray-500 dark:text-gray-500 text-xs mt-1">
              Vendors can be assigned later if needed
            </div>
          </div>
        )}
      </div>

      {/* Work Order Details Form */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">Work Order Details</h3>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Assigned User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assigned User
              </label>
              <Controller
                name="assignedUserId"
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...users
                        .filter((user: CompanyUser) => !!user.id)
                        .map((user: CompanyUser) => ({
                          value: user.id,
                          label: `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unknown user',
                        })),
                    ]}
                    value={field.value || ''}
                    onChange={(value) => field.onChange(String(value || ''))}
                    placeholder="Select a user"
                    searchPlaceholder="Search users..."
                    disabled={usersLoading}
                    error={errors.assignedUserId?.message}
                    aria-label="Assigned user"
                  />
                )}
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <PriorityDropdown
                name="priorityId"
                control={control}
                error={errors.priorityId}
                required={true}
                placeholder="Select Priority"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <Controller
                name="statusId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                  >
                    <option value="0">Select Status</option>
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.statusId && (
                <p className="mt-1 text-sm text-red-600">{errors.statusId.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <Controller
                name="categoryId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select a category (optional)</option>
                    {categories.map((category: Category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {categoriesError && (
                <p className="mt-1 text-sm text-red-600">Error loading categories</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Choose a category to classify this work order
              </p>
            </div>

            {/* Equipment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Equipment
              </label>
              <Controller
                name="equipmentId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                    disabled={equipmentLoading}
                  >
                    <option value="">Select equipment (optional)</option>
                    {equipment.map((eq: any) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} - {eq.clientLocationName || 'No Location'}
                      </option>
                    ))}
                  </select>
                )}
              />
              {equipmentError && (
                <p className="mt-1 text-sm text-red-600">Error loading equipment</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Link this work order to a specific equipment
              </p>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Due Date <span className="text-red-500">*</span>
              </label>
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                  />
                )}
              />
              {errors.dueDate && (
                <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p>
              )}
            </div>

            {/* External Work Order Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                External Work Order Number
              </label>
              <Controller
                name="externalWorkOrderNumber"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter external work order number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color placeholder-gray-400 dark:placeholder-gray-500"
                  />
                )}
              />
            </div>

            

            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Work Order Template
              </label>
              <Controller
                name="templateId"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                    disabled={templatesLoading}
                  >
                    <option value="">Select a template (optional)</option>
                    {templates.map((template: WorkOrderTemplate) => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              />
              {templatesError && (
                <p className="mt-1 text-sm text-red-600">Error loading templates</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Choose a template to format the work order document
              </p>
            </div>

            {/* NTE Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                NTE Amount (Not To Exceed)
              </label>
              <Controller
                name="nteAmount"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                    </div>
                    <input
                      {...field}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color placeholder-gray-400 dark:placeholder-gray-500"
                      value={field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  </div>
                )}
              />
              {errors.nteAmount && (
                <p className="mt-1 text-sm text-red-600">{errors.nteAmount.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Set a maximum amount for this work order (optional)
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <WYSIWYGEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter work order description..."
                />
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Optional: Add a description for this work order
            </p>
          </div>
        </form>
      </div>

             {/* Validation Summary */}
       <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
         <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Validation Status</h4>
         <div className="space-y-1 text-sm">
           <div className={`flex items-center ${watchedValues.assignedUserId ? 'text-green-600' : 'text-gray-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.assignedUserId ? 'text-green-600' : 'text-gray-600'}`} />
             {watchedValues.assignedUserId ? 'User assigned' : 'No user assigned (optional)'}
           </div>
           <div className={`flex items-center ${watchedValues.priorityId ? 'text-green-600' : 'text-red-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.priorityId ? 'text-green-600' : 'text-red-600'}`} />
             {watchedValues.priorityId ? `Priority selected` : 'Priority not set'}
           </div>
           <div className={`flex items-center ${watchedValues.statusId && watchedValues.statusId > 0 ? 'text-green-600' : 'text-red-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.statusId && watchedValues.statusId > 0 ? 'text-green-600' : 'text-red-600'}`} />
             {watchedValues.statusId && watchedValues.statusId > 0 ? `Status set to ${statusOptions.find(s => s.value === watchedValues.statusId)?.label || 'Unknown'}` : 'Status not set'}
           </div>
           <div className={`flex items-center ${watchedValues.categoryId ? 'text-green-600' : 'text-gray-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.categoryId ? 'text-green-600' : 'text-gray-600'}`} />
             {watchedValues.categoryId ? `Category selected` : 'No category selected (optional)'}
           </div>
           <div className={`flex items-center ${watchedValues.dueDate ? 'text-green-600' : 'text-red-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.dueDate ? 'text-green-600' : 'text-red-600'}`} />
             {watchedValues.dueDate ? `Due date set to ${watchedValues.dueDate}` : 'Due date not set'}
           </div>
           <div className={`flex items-center ${getTextLength(watchedValues.description || '') > 0 ? 'text-green-600' : 'text-gray-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${getTextLength(watchedValues.description || '') > 0 ? 'text-green-600' : 'text-gray-600'}`} />
             {getTextLength(watchedValues.description || '') > 0 ? 'Description provided' : 'No description (optional)'}
           </div>
           <div className={`flex items-center ${watchedValues.templateId ? 'text-green-600' : 'text-gray-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.templateId ? 'text-green-600' : 'text-gray-600'}`} />
             {watchedValues.templateId ? 'Template selected' : 'No template selected (optional)'}
           </div>
           <div className={`flex items-center ${watchedValues.nteAmount ? 'text-green-600' : 'text-gray-600'}`}>
             <Check className={`h-4 w-4 mr-2 ${watchedValues.nteAmount ? 'text-green-600' : 'text-gray-600'}`} />
             {watchedValues.nteAmount ? `NTE amount set to $${watchedValues.nteAmount.toFixed(2)}` : 'No NTE amount set (optional)'}
           </div>
         </div>
       </div>

      {/* Navigation */}
      <div className="flex justify-between space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleBack}
          className="btn-secondary"
        >
          Back to Step 2
        </button>
        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || !isValid}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

export default StepDetails;
